import { useEffect, useState } from 'react'
import { apiFetch } from '../app/auth/apiClient'

export interface AceSession {
  token: string
  expires_at: string
  expires_in: number
  ace_tenant_id: string
  ace_api_url: string
  ace_dashboard_url: string
  default_assistant: string
  proposal_editor_assistant: string
  acting_token_header: string
  tenant_header: string
  advisor_code: string
}

type AceSessionState =
  | { status: 'loading'; session: null; error: null }
  | { status: 'ready'; session: AceSession; error: null }
  | { status: 'error'; session: null; error: string }

export function useAceSession(): AceSessionState {
  const [state, setState] = useState<AceSessionState>({
    status: 'loading',
    session: null,
    error: null,
  })

  useEffect(() => {
    const controller = new AbortController()
    setState({ status: 'loading', session: null, error: null })

    apiFetch('/api/auth/ace/session', {
      method: 'POST',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => null)
          throw new Error(payload?.error || `HTTP ${response.status}`)
        }
        return response.json() as Promise<AceSession>
      })
      .then((session) => {
        if (!controller.signal.aborted) setState({ status: 'ready', session, error: null })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setState({
          status: 'error',
          session: null,
          error: error instanceof Error ? error.message : 'ACE session is not available',
        })
      })

    return () => controller.abort()
  }, [])

  return state
}
